/**
 * MarketTrendsChart Component Types
 *
 * A single combined line chart for the GeographyModal "Market Data" tab -
 * a sibling to SalesChart, not an extension of it. SalesChart is built
 * around per-transaction scatter points on a single price axis with a
 * regression overlay; this renders pre-aggregated monthly series with
 * genuinely different native units (days, a ratio, a month-count, a
 * percentage). Rather than a dual/multi-axis chart (the #1 charting
 * mistake per the dataviz skill) or small multiples, each metric is
 * indexed to a shared scale via its own threshold bands (see utils.ts) -
 * +1 = at its seller threshold, -1 = at its buyer threshold, 0 = balanced
 * - so every line shares one meaningful axis: Seller's Market at top,
 * Buyer's Market at bottom, Balanced in the middle. An equal-weight
 * "Overall" line averages every metric with data; each metric (and
 * Overall) has its own checkbox so a reader can isolate any subset.
 *
 * Also owns a Condo/House/All property-type selector (mirrors SalesChart's
 * self-contained filter-row convention - time period/bedrooms/stories live
 * inside SalesChart itself, not the parent modal). All three segments
 * arrive in one fetch (data-api joins mls_listings.property_subtype,
 * falling back to tmk_records.is_condo when that's null); switching
 * segments re-renders from the already-fetched array for that segment,
 * no network call.
 */
export const PROPERTY_SEGMENTS = [
    { key: 'all', label: 'All' },
    { key: 'condo', label: 'Condo' },
    { key: 'house', label: 'House' },
];
/**
 * Buyer's-market / seller's-market status colors. Blue -> gray -> orange
 * ("cool" to "hot" market temperature) rather than the classic red/green
 * status pairing - deuteranopia/protanopia (the common forms of color
 * blindness) confuse red and green specifically, but not blue and orange,
 * so this reads correctly for CVD viewers without relying on a secondary
 * encoding. Also doubles as a literal "market temperature" metaphor, which
 * fits the subject better than a generic traffic-light scheme.
 */
export const MARKET_STATUS_COLORS = {
    buyer: '#3b82f6', // blue - matches the existing "For Sale" blue in SalesChart's CHART_COLORS
    balanced: '#94a3b8', // slate gray
    seller: '#ea580c', // orange
    line: '#1f2937',
    gridLines: '#e5e7eb',
};
/**
 * Thresholds recalibrated against Maui's own trailing-24-month distribution
 * (terciles across all 11 regions/districts, 183 non-null data points),
 * not generic national real-estate benchmarks. Todd's original numbers
 * (DOM seller<60/buyer>100, sale-to-list buyer<97%/seller>100%, price
 * drops seller<10%/buyer>20%) and the standard national Months-of-Supply
 * convention (seller<4/buyer>6) were checked against the real computed
 * data first, per the plan's verification step - every one of them would
 * have shown virtually the entire island in a permanent deep buyer's
 * market with zero discriminating power (e.g. the P33-P66 range for
 * Months of Supply alone was 11.7-17.3, already 2-3x past the "buyer"
 * threshold of 6). Maui's vacation/second-home market genuinely moves
 * much slower than mainland norms across every one of these metrics, not
 * just DOM. These bands instead answer "is this scope moving faster or
 * slower than Maui's own typical pace right now" - terciles of the actual
 * distribution, seller = bottom third (fastest-moving/tightest), buyer =
 * top third (slowest-moving/loosest). Revisit once more history accrues
 * (24 months is a thin sample for percentile boundaries).
 *
 * SNLR (Sales-to-New-Listings Ratio) uses Todd's own thresholds (buyer<40%,
 * seller>60%) rather than a recalibrated one - checked first anyway (real
 * P33/P50/P66 across 343 points: 0% / 22% / 46%), and unlike the other
 * four metrics there's genuine spread reaching into seller territory (max
 * 138%), not universal saturation at one extreme - so it's a real signal
 * even though, like everything else here, most periods still read buyer-
 * leaning today.
 *
 * Months of Supply's balanced band was hand-adjusted by Todd on 2026-09-02
 * (9-12mo, from the originally data-derived 12-17mo) - a deliberate override
 * of the tercile calibration above, not re-derived from a new data check.
 *
 * Sale-to-List's balanced band went through two revisions the same day:
 * first hand-adjusted to 95-98% (from the original 94-95%), but that was
 * against the OLD ratio definition (close price / list price at time of
 * sale). The same day, the ratio itself was redefined to close price /
 * ORIGINAL list price (captures full cumulative discounting, not just the
 * final negotiation) - which shifts the whole distribution down, since
 * original list price is always >= list price at time of sale. 95-98%
 * against the new distribution was degenerate (58 of 60 scope-months read
 * buyer's-market, none reached 98%), so it was replaced with 88-90%,
 * derived from the new distribution's real P33-P66 (88.5%-90.2%). Revisit
 * this note if the ratio definition or band ever changes again.
 */
export const METRIC_CONFIGS = [
    {
        key: 'medianDaysOnMarket',
        label: 'Median Days on Market',
        format: (v) => `${Math.round(v)} days`,
        bands: { sellerThreshold: 90, buyerThreshold: 120, polarity: 'lowIsSeller' },
        color: '#7c3aed', // violet
        dash: [],
        description: 'The midpoint of how long it takes an active listing to go under contract. Lower means homes are moving faster (favors sellers); higher means buyers have more time to negotiate.',
    },
    {
        key: 'monthsOfSupply',
        label: 'Months of Supply',
        format: (v) => `${v.toFixed(1)} mo`,
        bands: { sellerThreshold: 9, buyerThreshold: 12, polarity: 'lowIsSeller' },
        color: '#0891b2', // teal
        dash: [6, 3],
        description: 'How many months it would take to sell off all current active inventory at the recent sales pace. Lower means inventory is tight (favors sellers); higher means buyers have more homes to choose from.',
    },
    {
        key: 'saleToListRatio',
        label: 'Sale-to-List Ratio',
        format: (v) => `${(v * 100).toFixed(1)}%`,
        bands: { sellerThreshold: 0.90, buyerThreshold: 0.88, polarity: 'highIsSeller' },
        color: '#be123c', // rose
        dash: [8, 3, 2, 3], // dot-dash
        description: 'The final sale price as a percentage of the ORIGINAL list price (not whatever the list price happened to be at the moment it sold), so it captures the full discount from the initial ask. Higher means homes are selling close to (or above) asking (favors sellers); lower means more total negotiating room.',
    },
    {
        key: 'pctPriceDrops',
        label: '% Listings with a Price Drop',
        format: (v) => `${(v * 100).toFixed(0)}%`,
        bands: { sellerThreshold: 0.45, buyerThreshold: 0.55, polarity: 'lowIsSeller' },
        color: '#a16207', // amber
        dash: [2, 2], // dotted
        description: 'The share of listings on the market during the period that have had at least one price cut. Lower means sellers are holding firm on price (favors sellers); higher means more sellers are conceding.',
    },
    {
        key: 'snlr',
        label: 'Sales-to-New-Listings Ratio',
        format: (v) => `${(v * 100).toFixed(0)}%`,
        bands: { sellerThreshold: 0.6, buyerThreshold: 0.4, polarity: 'highIsSeller' },
        color: '#059669', // emerald
        dash: [1, 3], // fine dots
        description: 'Homes sold this period as a share of new listings that came on the market. Higher means demand is outpacing new supply (favors sellers); lower means new listings are piling up faster than they sell.',
    },
];
/** Visual config for the equal-weight composite line - always averages the
 * index values of every metric that has data, regardless of which
 * individual lines are checked for display (see MarketTrendsChart's
 * computeOverallSeries). Deliberately not in METRIC_CONFIGS - it has no
 * raw value/format of its own and isn't one of the underlying signals. */
export const OVERALL_CONFIG = {
    label: 'Overall (avg. of metrics below)',
    color: '#111827', // near-black, reads as the "headline" line
    lineWidth: 3,
};
//# sourceMappingURL=types.js.map